import React from "react"
import { Box, Card, CardContent, Typography } from "@mui/material"
import { type LucideIcon } from "lucide-react"

export interface StatsCardProps {
  title: string
  value: string | number
  icon?: LucideIcon
  iconColor?: string
  valueColor?: string
  customIcon?: React.ReactNode
  sx?: import("@mui/material").SxProps<import("@mui/material").Theme>
}

// Eight gradient choices, seeded deterministically by the card title so
// the top-border color is stable across re-renders. Math.random() used
// to run on every render, which made each card's stripe flicker through
// every color whenever React redrew the list — distracting and unfit
// for a static stat. (Was Tailwind `bg-gradient-to-r from-X to-Y`; baked
// to literal CSS gradients here so the look is unchanged after the MUI
// migration drops Tailwind.)
const COLOR_LINES = [
  "linear-gradient(to right, #3b82f6, #a855f7)", // blue → purple
  "linear-gradient(to right, #22c55e, #14b8a6)", // green → teal
  "linear-gradient(to right, #f97316, #ef4444)", // orange → red
  "linear-gradient(to right, #a855f7, #ec4899)", // purple → pink
  "linear-gradient(to right, #6366f1, #3b82f6)", // indigo → blue
  "linear-gradient(to right, #10b981, #06b6d4)", // emerald → cyan
  "linear-gradient(to right, #f43f5e, #f97316)", // rose → orange
  "linear-gradient(to right, #8b5cf6, #a855f7)", // violet → purple
]

// The CRM passes stat-card icon/value colors as Tailwind text-color class
// strings (decorative, varied — not semantic palette roles). Baked to the
// exact sRGB hex the utilities produced so the 16 call sites stay unchanged
// after Tailwind is removed. Unknown values fall through (allows a raw color).
const TW_TEXT_COLOR: Record<string, string> = {
  "text-blue-600": "#2563eb",
  "text-blue-500": "#3b82f6",
  "text-green-600": "#16a34a",
  "text-green-500": "#22c55e",
  "text-emerald-600": "#059669",
  "text-purple-600": "#9333ea",
  "text-indigo-500": "#6366f1",
  "text-red-600": "#dc2626",
  "text-red-500": "#ef4444",
  "text-orange-600": "#ea580c",
  "text-amber-600": "#d97706",
  "text-yellow-600": "#ca8a04",
  "text-yellow-500": "#eab308",
  "text-gray-900": "#111827",
  "text-gray-600": "#4b5563",
}

function resolveColor(c: string | undefined, fallback: string): string {
  if (!c) return fallback
  return TW_TEXT_COLOR[c] ?? c
}

function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

export function StatsCard({
  title,
  value,
  icon: Icon,
  iconColor = "text-blue-600",
  valueColor = "text-gray-900",
  customIcon,
  sx,
}: StatsCardProps) {
  const colorLine = COLOR_LINES[hashString(title) % COLOR_LINES.length]

  return (
    <Card sx={{ position: "relative", overflow: "hidden", height: "100%", ...sx }}>
      {/* Stable top border — color is derived from the card title via
          hashString() so it stays the same across re-renders. Inline
          style (not sx) so the computed gradient is readable in tests. */}
      <Box data-testid="stats-stripe" sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4 }} style={{ background: colorLine }} />

      {/* Real vertical padding so the label clears the 4px stripe and the
          value clears the bottom edge. Was `py: 0` + `maxHeight: 76`, which
          crammed the text flush against both edges and capped the height. */}
      <CardContent sx={{ px: 2, py: 1.75, height: "100%", "&:last-child": { pb: 1.75 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "#4b5563" }}>{title}</Typography>
            <Typography sx={{ fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.35, color: resolveColor(valueColor, "#111827") }}>{value}</Typography>
          </Box>
          {customIcon && customIcon}
          {Icon && !customIcon && <Icon size={26} color={resolveColor(iconColor, "#2563eb")} />}
        </Box>
      </CardContent>
    </Card>
  )
}

// A stat card that carries TWO numbers under one title (e.g. the
// Customers header's "Ports" card: audio + web). It reuses the same
// shell — 4px deterministic stripe, padding, icon slot — so it sits
// flush in the stat grid beside the single-value cards; only the value
// row differs, rendering each number with its own small label.
export interface StatsCardDualItem {
  label: string
  value: string | number
  valueColor?: string
}

export interface StatsCardDualProps {
  title: string
  items: [StatsCardDualItem, StatsCardDualItem]
  icon?: LucideIcon
  iconColor?: string
  sx?: import("@mui/material").SxProps<import("@mui/material").Theme>
}

export function StatsCardDual({ title, items, icon: Icon, iconColor = "text-blue-600", sx }: StatsCardDualProps) {
  const colorLine = COLOR_LINES[hashString(title) % COLOR_LINES.length]

  return (
    <Card sx={{ position: "relative", overflow: "hidden", height: "100%", ...sx }}>
      <Box data-testid="stats-stripe" sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4 }} style={{ background: colorLine }} />

      <CardContent sx={{ px: 2, py: 1.75, height: "100%", "&:last-child": { pb: 1.75 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "#4b5563" }}>{title}</Typography>
            {/* Both numbers sit on ONE line so the card keeps the same
                two-line height as its single-value siblings. The value
                is a step smaller than a single-value card's (1.125rem
                vs 1.375rem) because two of them share the width — at
                the full size a four-digit port count wrapped the second
                pair onto its own line and stretched the whole grid row.
                No separator glyph: it orphans onto the next line on the
                narrow breakpoints where the pairs DO wrap. */}
            <Box sx={{ display: "flex", alignItems: "baseline", columnGap: 1.25, rowGap: 0, flexWrap: "wrap" }}>
              {items.map((item) => (
                <Box key={item.label} sx={{ display: "flex", alignItems: "baseline", gap: 0.375 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: "1.125rem", fontWeight: 700, lineHeight: 1.55, color: resolveColor(item.valueColor, "#111827") }}
                  >
                    {item.value}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "#4b5563" }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {Icon && <Icon size={26} color={resolveColor(iconColor, "#2563eb")} />}
        </Box>
      </CardContent>
    </Card>
  )
}

// Colored dot icon component for consistency
export function ColoredDot({ color }: { color: string }) {
  // `color` is a Tailwind bg-* class string from callers; bake to hex.
  const TW_BG: Record<string, string> = {
    "bg-blue-100": "#dbeafe",
    "bg-green-100": "#dcfce7",
    "bg-red-100": "#fee2e2",
    "bg-yellow-100": "#fef9c3",
    "bg-purple-100": "#f3e8ff",
    "bg-orange-100": "#ffedd5",
    "bg-gray-100": "#f3f4f6",
  }
  const bg = TW_BG[color] ?? color
  return (
    <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }} style={{ backgroundColor: bg }}>
      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "currentColor" }} />
    </Box>
  )
}

// Dollar icon component for consistency
export function DollarIcon() {
  return (
    <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#4b5563" }}>$</Typography>
    </Box>
  )
}
